/**
 * What the portal asks, in what order, and what counts as finished.
 *
 * The section list is derived from the application rather than fixed: a Class I
 * applicant is never shown an academic-history table or a subject chooser, so
 * "Section 3 of 4" means three of the sections that actually apply to her. The
 * field shapes match the answers blob the API validates
 * (backend/src/services/admissionAnswers.js) — this file must not invent keys
 * the schema will strip.
 */
import { subjectLevel } from "components/admissions/SubjectFields";
import { needsEducation } from "components/admissions/AdmissionFields";

/* ------------------------------------------------------------------ helpers */

/** Read "guardian.phone" out of the answers blob. */
export const getIn = (answers, path) =>
  path.split(".").reduce((v, k) => (v == null ? v : v[k]), answers);

/** Write "guardian.phone" without mutating, so React sees a new object. */
export function setIn(answers, path, value) {
  const [head, ...rest] = path.split(".");
  if (!rest.length) return { ...answers, [head]: value };
  return { ...answers, [head]: setIn(answers[head] || {}, rest.join("."), value) };
}

const filled = (v) => (Array.isArray(v) ? v.some(Boolean) : String(v ?? "").trim() !== "");

/* ----------------------------------------------------------------- sections */

/**
 * Sections for this application. `fields` lists every field id the section
 * owns, and `required` the subset that must be answered before it counts as
 * complete — the same ids the error summary links to.
 */
export function buildSections({ institution, classApplied }) {
  const list = [
    {
      key: "student",
      bn: "শিক্ষার্থীর তথ্য",
      en: "Student information",
      fields: ["applicant.nameBn", "applicant.nameEn", "dob", "religion", "nationality"],
      required: ["applicant.nameBn", "dob"],
    },
    {
      key: "family",
      bn: "পিতা-মাতা ও অভিভাবক",
      en: "Parents and guardian",
      fields: [
        "father.nameBn",
        "father.nameEn",
        "mother.nameBn",
        "mother.nameEn",
        "guardian.relation",
        "guardian.name",
        "guardian.occupation",
        "guardian.workAddress",
        "guardian.phone",
        "guardian.monthlyIncome",
      ],
      required: ["father.nameBn", "mother.nameBn", "guardian.occupation", "guardian.phone"],
    },
    {
      key: "address",
      bn: "ঠিকানা ও শিক্ষাগত তথ্য",
      en: "Address and education",
      fields: ["present.address", "present.phone", "permanent.address", "education"],
      required: ["present.address"],
      // Only classes that follow another stage of schooling are asked for one.
      education: needsEducation(institution, classApplied || ""),
    },
  ];

  // Nursery, primary and honours applicants choose no subjects here.
  const level = subjectLevel(institution, classApplied || "");
  if (level) {
    list.push({
      key: "subjects",
      bn: "বিষয় নির্বাচন",
      en: "Subject selection",
      fields: ["subjects.compulsory", "subjects.optional"],
      required: [],
      level,
    });
  }

  list.push({
    key: "review",
    bn: "নথি, পর্যালোচনা ও ঘোষণা",
    en: "Documents, review and declaration",
    fields: ["declarations.applicant", "declarations.guardian"],
    required: ["declarations.applicant", "declarations.guardian"],
    review: true,
  });

  return list;
}

/** Not started / in progress / complete, for the section checklist. */
export function sectionStatus(section, answers) {
  const touched = section.fields.some((f) => filled(getIn(answers, f)));
  if (!touched) return "todo";
  return section.required.every((f) => filled(getIn(answers, f))) ? "complete" : "progress";
}

/* --------------------------------------------------------------- validation */

/** Bangla or ASCII digits; must look like a Bangladeshi mobile number. */
export function validatePhone(raw) {
  const ascii = String(raw || "").replace(/[০-৯]/g, (d) => String("০১২৩৪৫৬৭৮৯".indexOf(d)));
  const digits = ascii.replace(/[\s-]/g, "");
  if (!digits) return "required";
  if (!/^(\+?880|0)1[3-9]\d{8}$/.test(digits)) return "format";
  return null;
}

/**
 * Problems in one section, as { id, message } for the error summary.
 *
 * Validation runs on Save & continue, not while typing: a half-typed phone
 * number is not an error yet. `t` is the caller's translator so the messages
 * come back in the language on screen.
 */
export function validateSection(section, answers, t) {
  const found = [];
  const need = (id, message) => {
    if (!filled(getIn(answers, id))) found.push({ id, message });
  };

  if (section.key === "student") {
    need(
      "applicant.nameBn",
      t("শিক্ষার্থীর নাম (বাংলায়) লিখুন", "Enter the student's name in Bangla")
    );
    const dob = getIn(answers, "dob");
    if (!filled(dob)) {
      found.push({ id: "dob", message: t("জন্ম তারিখ লিখুন", "Enter the date of birth") });
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(dob) || new Date(dob) > new Date()) {
      found.push({
        id: "dob",
        message: t("জন্ম তারিখটি সঠিক নয়", "That date of birth is not valid"),
      });
    }
  }

  if (section.key === "family") {
    need("father.nameBn", t("পিতার নাম (বাংলায়) লিখুন", "Enter the father's name in Bangla"));
    need("mother.nameBn", t("মাতার নাম (বাংলায়) লিখুন", "Enter the mother's name in Bangla"));
    need("guardian.occupation", t("অভিভাবকের পেশা লিখুন", "Enter the guardian's occupation"));

    const problem = validatePhone(getIn(answers, "guardian.phone"));
    if (problem === "required")
      found.push({
        id: "guardian.phone",
        message: t("মোবাইল নম্বর লিখুন", "Enter a mobile number"),
      });
    else if (problem === "format")
      found.push({
        id: "guardian.phone",
        message: t(
          "মোবাইল নম্বরটি সঠিক নয় — যেমন ০১৭১২৩৪৫৬৭৮",
          "That mobile number is not valid — for example 01712345678"
        ),
      });
  }

  if (section.key === "address") {
    need("present.address", t("বর্তমান ঠিকানা লিখুন", "Enter the present address"));
    // A contact number here is optional, but a wrong one is still worth saying.
    const phone = getIn(answers, "present.phone");
    if (filled(phone) && validatePhone(phone) === "format")
      found.push({
        id: "present.phone",
        message: t("যোগাযোগ নম্বরটি সঠিক নয়", "That contact number is not valid"),
      });
  }

  if (section.key === "review") {
    if (!getIn(answers, "declarations.applicant"))
      found.push({
        id: "declarations.applicant",
        message: t("শিক্ষার্থীর ঘোষণায় সম্মতি দিন", "Agree to the student's declaration"),
      });
    if (!getIn(answers, "declarations.guardian"))
      found.push({
        id: "declarations.guardian",
        message: t("অভিভাবকের ঘোষণায় সম্মতি দিন", "Agree to the guardian's declaration"),
      });
  }

  return found;
}

/**
 * Sections still blocking submission, so Review can say what is missing instead
 * of letting the family press Submit and be refused by the server.
 */
export const incompleteSections = (sections, answers) =>
  sections.filter((s) => !s.review && sectionStatus(s, answers) !== "complete");
